import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Send, Camera, X, Loader2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

function dueDateLabel(noDueDate, dueDate) {
  if (noDueDate) return "N/A";
  if (dueDate) return format(dueDate, "PPP");
  return "Pick a due date";
}

export default function WorkOrderForm({ api, meta, onCreated, defaultName, isAdmin }) {
  const [property, setProperty] = useState("");
  const [issueType, setIssueType] = useState("");
  const [officeArea, setOfficeArea] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [noDueDate, setNoDueDate] = useState(false);
  const [priority, setPriority] = useState("Medium");
  const [description, setDescription] = useState("");
  const [requesterName, setRequesterName] = useState(defaultName || "");
  const [photos, setPhotos] = useState([]); // [{path,url}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isProject, setIsProject] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 5) {
      toast.error("Max 5 photos per request");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${api}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      setPhotos((p) => [...p, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} attached`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!property || !issueType || !officeArea.trim() || !companyName.trim() || !description.trim() || !requesterName.trim()) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!noDueDate && !dueDate) {
      toast.error("Select a due date or check 'No specific due date'");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${api}/work-orders`, {
        property,
        issue_type: issueType,
        office_area: officeArea,
        company_name: companyName,
        due_date: noDueDate ? null : format(dueDate, "yyyy-MM-dd"),
        no_due_date: noDueDate,
        priority,
        description,
        requester_name: requesterName || null,
        photos: photos.map((p) => p.path),
        is_project: isAdmin && isProject,
      });
      onCreated?.();
      setProperty(""); setIssueType(""); setOfficeArea(""); setCompanyName("");
      setDueDate(null); setNoDueDate(false); setPriority("Medium");
      setDescription(""); setRequesterName(defaultName || ""); setPhotos([]); setIsProject(false);
    } catch (err) {
      toast.error("Failed to submit work order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2 border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Intake</div>
          <CardTitle className="text-2xl tracking-tight">File a Maintenance Request</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-6" data-testid="work-order-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Property *</Label>
                <Select value={property} onValueChange={setProperty}>
                  <SelectTrigger className="mt-2 bg-white" data-testid="property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {meta.properties?.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`property-option-${p.replace(/\s+/g, "-")}`}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Issue Type *</Label>
                <Select value={issueType} onValueChange={setIssueType}>
                  <SelectTrigger className="mt-2 bg-white" data-testid="issue-type-select"><SelectValue placeholder="Select issue type" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {meta.issue_types?.map((i) => (<SelectItem key={i} value={i} data-testid={`issue-option-${i.replace(/\s+/g, "-")}`}>{i}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Office Number / Area *</Label>
                <Input required className="mt-2 bg-white" placeholder="e.g. Suite 402, North Wing Hot Desks" value={officeArea} onChange={(e) => setOfficeArea(e.target.value)} data-testid="office-area-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Company Name * <span className="normal-case font-normal text-slate-400" data-testid="company-name-hint">(Enter WP1 for Workplace one)</span></Label>
                <Input required className="mt-2 bg-white" placeholder="e.g. Acme Studio" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="company-name-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Due Date</Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" disabled={noDueDate} className="bg-white justify-start min-w-[240px] font-normal" data-testid="due-date-trigger">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dueDateLabel(noDueDate, dueDate)}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus disabled={noDueDate} />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2">
                    <Checkbox id="no-due" checked={noDueDate} onCheckedChange={(v) => { setNoDueDate(!!v); if (v) setDueDate(null); }} data-testid="na-due-date-checkbox" />
                    <label htmlFor="no-due" className="text-sm text-slate-700 cursor-pointer select-none">No specific due date (N/A) — overrides calendar</label>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="mt-2 bg-white" data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {PRIORITIES.map((p) => (<SelectItem key={p} value={p} data-testid={`priority-option-${p}`}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Requester Name *</Label>
                <Input required className="mt-2 bg-white" placeholder="Pick or type a name" value={requesterName} onChange={(e) => setRequesterName(e.target.value)} list="requester-names" data-testid="requester-name-input" />
                <datalist id="requester-names">
                  {meta.requester_names?.map((n) => (<option key={n} value={n} />))}
                </datalist>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Description of the Issue or Task *</Label>
              <Textarea className="mt-2 bg-white min-h-[140px]" placeholder="Describe the issue in detail — location, what is happening, any hazards or urgency..." value={description} onChange={(e) => setDescription(e.target.value)} data-testid="description-textarea" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Photos (optional, up to 5)</Label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {photos.map((p) => (
                  <div key={p.path} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200 bg-slate-100">
                    <img src={p.url} alt="attachment" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(p.path)} className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-md p-0.5" data-testid={`remove-photo-${p.path}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <label className="w-20 h-20 rounded-md border border-dashed border-slate-300 bg-slate-50 grid place-items-center cursor-pointer hover:bg-slate-100 text-slate-500">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                    <input type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="photo-upload-input" />
                  </label>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">Snap a photo of the issue so the technician sees it before arriving. JPG/PNG/WEBP, max 8MB each.</p>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-2 rounded-md border-2 border-red-500 bg-red-50 p-3" data-testid="project-toggle-row">
                <Checkbox id="is-project" checked={isProject} onCheckedChange={(v) => setIsProject(!!v)} data-testid="is-project-checkbox" />
                <label htmlFor="is-project" className="text-sm text-red-800 cursor-pointer select-none">
                  <strong>Submit as Project</strong> (admin only)
                </label>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
              <Button type="submit" disabled={submitting} className="bg-[#478261] hover:bg-[#3a6b51] text-white px-6" data-testid="submit-work-order-button">
                <Send className="w-4 h-4 mr-2" />{submitting ? "Submitting..." : "Submit Request"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm h-fit">
        <CardHeader className="border-b border-slate-100">
          <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Operations</div>
          <CardTitle className="text-lg tracking-tight">How it works</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4 text-sm text-slate-600">
          <div className="flex gap-3"><div className="w-6 h-6 rounded-md bg-slate-900 text-white grid place-items-center text-xs font-mono">1</div><p>Submit a request with property, area, issue type and a photo if handy.</p></div>
          <div className="flex gap-3"><div className="w-6 h-6 rounded-md bg-slate-900 text-white grid place-items-center text-xs font-mono">2</div><p>Facilities triages and updates status — you get an email every time your request's status changes.</p></div>
          <div className="flex gap-3"><div className="w-6 h-6 rounded-md bg-slate-900 text-white grid place-items-center text-xs font-mono">3</div><p>Daily digest + Friday weekly recap emailed to <span className="font-mono">d.viola@workplaceone.com</span>.</p></div>

          <button
            type="button"
            onClick={() => setShowTutorial((v) => !v)}
            className="w-full text-left rounded-md border border-[#478261] bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-[#2c4f3c] hover:bg-emerald-100 transition-colors flex items-center justify-between"
            data-testid="tutorial-toggle-button"
          >
            <span className="inline-flex items-center gap-2"><GraduationCap className="w-4 h-4" />New here? Full walkthrough — what to do &amp; what to expect</span>
            <span className="font-mono text-xs">{showTutorial ? "−" : "+"}</span>
          </button>

          {showTutorial && (
            <div className="rounded-md border border-slate-200 bg-white p-4 space-y-4" data-testid="tutorial-panel">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#478261] mb-2">How to enter a work order — step by step</div>
                <ol className="space-y-2 text-sm text-slate-700 list-none">
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">1.</span><span><strong>Property</strong> — pick your building from the dropdown (e.g. TERRIER - 340 King East).</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">2.</span><span><strong>Issue Type</strong> — choose the closest match (Plumbing, Electrical, Lighting, etc.).</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">3.</span><span><strong>Office Number / Area</strong> and <strong>Company Name</strong> — type exactly where and for whom, e.g. "Suite 402, Acme Studio".</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">4.</span><span><strong>Due Date</strong> — pick a date from the calendar, or tick <strong>N/A</strong> if there's no deadline.</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">5.</span><span><strong>Priority</strong> — Low / Medium / High / Urgent. Use Urgent only for safety hazards or total outages.</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">6.</span><span><strong>Description</strong> — what happened, where exactly, anything the technician should know.</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">7.</span><span><strong>Photos</strong> (optional) — attach up to 5 photos of the issue.</span></li>
                  <li className="flex gap-2.5"><span className="font-mono font-bold text-[#478261] shrink-0">8.</span><span>Hit <strong>Submit Request</strong> — done! You'll get a ticket number like WO-TOR-1024.</span></li>
                </ol>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#478261] mb-2">What to expect after you submit</div>
                <ul className="space-y-1.5 text-sm text-slate-700 list-none">
                  <li className="flex gap-2"><span className="text-[#478261]">•</span>Facilities is emailed instantly (Terrier properties go straight to the property manager).</li>
                  <li className="flex gap-2"><span className="text-[#478261]">•</span>You get an email every time the status changes — In Progress, On Hold (with a comment), or Completed (with notes).</li>
                  <li className="flex gap-2"><span className="text-[#478261]">•</span><span>Track everything under the{" "}<strong>My Requests</strong>{" "}tab.</span></li>
                  <li className="flex gap-2"><span className="text-[#478261]">•</span><span>Same issue again? Hit{" "}<strong>Duplicate</strong>{" "}on My Requests to re-file it in one click.</span></li>
                  <li className="flex gap-2"><span className="text-[#478261]">•</span><span>Questions? Email{" "}<span className="font-mono">d.viola@workplaceone.com</span>.</span></li>
                </ul>
              </div>
            </div>
          )}

          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">Tip: Check <strong>N/A</strong> when there is no fixed deadline — the calendar is disabled automatically.</div>
        </CardContent>
      </Card>
    </div>
  );
}
